/**
 * 白板的双指缩放：纯几何，不碰 DOM，方便单测。
 *
 * 视图用「先缩放再平移」表示：屏幕点 = 内容点 × scale + (tx, ty)。
 * 缩放时要保持**两指中点下面的内容不动** —— 不做这一步的话，
 * 每次捏合画面都会往左上角缩过去，手指底下的字跑掉了。
 */

export interface View {
  scale: number
  tx: number
  ty: number
}

export interface Pt {
  x: number
  y: number
}

export const IDENTITY: View = { scale: 1, tx: 0, ty: 0 }

export const MIN_SCALE = 1
export const MAX_SCALE = 4

/** 一次双指手势开始时记下的东西。 */
export interface PinchStart {
  view: View
  /** 两指中点（相对画布容器） */
  mid: Pt
  /** 两指间距 */
  dist: number
}

export function pinchStart(view: View, a: Pt, b: Pt): PinchStart {
  return { view, mid: midpoint(a, b), dist: Math.max(1, distance(a, b)) }
}

/**
 * 两指移动到 a、b 之后的新视图。
 *
 * box 是画布在 scale=1 时的尺寸，用来把平移夹在「内容不离开视口」的范围内。
 */
export function pinchMove(start: PinchStart, a: Pt, b: Pt, box: { w: number; h: number }): View {
  const mid = midpoint(a, b)
  const dist = Math.max(1, distance(a, b))
  const scale = clamp(start.view.scale * (dist / start.dist), MIN_SCALE, MAX_SCALE)

  // 手势开始时中点下面的内容点，缩放后仍要落在当前中点下面。
  const content = {
    x: (start.mid.x - start.view.tx) / start.view.scale,
    y: (start.mid.y - start.view.ty) / start.view.scale,
  }
  const tx = mid.x - content.x * scale
  const ty = mid.y - content.y * scale
  return clampView({ scale, tx, ty }, box)
}

/** 双击：没放大就放大到 2 倍（以点击处为中心），放大了就复位。 */
export function toggleZoomAt(view: View, at: Pt, box: { w: number; h: number }): View {
  if (view.scale > 1.01) return IDENTITY
  const scale = 2
  const content = { x: (at.x - view.tx) / view.scale, y: (at.y - view.ty) / view.scale }
  return clampView({ scale, tx: at.x - content.x * scale, ty: at.y - content.y * scale }, box)
}

/** 平移夹在内容覆盖视口的范围内：不许拖出一块空白。 */
export function clampView(v: View, box: { w: number; h: number }): View {
  const scale = clamp(v.scale, MIN_SCALE, MAX_SCALE)
  const minTx = box.w - box.w * scale
  const minTy = box.h - box.h * scale
  return { scale, tx: clamp(v.tx, minTx, 0), ty: clamp(v.ty, minTy, 0) }
}

function midpoint(a: Pt, b: Pt): Pt {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
}
function distance(a: Pt, b: Pt): number {
  return Math.hypot(a.x - b.x, a.y - b.y)
}
function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v
}
